import * as React from 'react'
import { useDropzone, DropzoneProps } from 'react-dropzone'
import { Icon, Typography } from '@material-ui/core'
import styled from 'styled-components'
import numeral from 'numeral'
import { useTranslation } from 'i18n'
import { acceptExt, hideAcceptTypes, uploadFileSize } from '../../contracts'

type Props = {
  image?: boolean
} & DropzoneProps

const Component: React.FC<Props> = ({ onDrop, accept, maxSize, image }) => {
  const { t } = useTranslation('components')
  const [error, setError] = React.useState(null)

  const onDropRejected = event => {
    setError(t('Input.Upload.fileNotAuthorized'))
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, ...props) => {
      setError(null)

      if (!acceptedFiles.length) {
        setError(t('Input.Upload.fileNotAuthorized'))
        return
      }

      const file = acceptedFiles[0]

      if (!validateFileSize(file)) {
        setError(t('Input.Upload.fileSizeExceeded'))
        return
      }

      return onDrop(acceptedFiles, ...props)
    },
    accept,
    onDropRejected,
  })

  return (
    <section>
      {error && (
        <div style={{ color: 'red', textAlign: 'center', padding: '5px 0' }}>
          {error}
        </div>
      )}
      <div {...getRootProps()}>
        <Area>
          <Icon style={{ color: '#CFD8DC' }}>add</Icon>
          <Typography>
            {t(`Input.Upload.${image ? 'imageUpload' : 'fileUpload'}`)}
          </Typography>
          <Typography variant="subtitle2">
            {t('Input.Upload.format')}: {acceptFiles(accept, hideAcceptTypes)}{' '}
            {maxSize &&
              `${t('Input.Upload.size')} : ${numeral(maxSize).format('0b')}`}
          </Typography>
        </Area>
        <input {...getInputProps()} />
      </div>
    </section>
  )
}

export default Component

/**
 * リソースアップロード領域の「形式」に表示するタイプを戻り値で返す
 * mimeTypeには必要だが「形式」に描画しない物はhideAcceptTypesでフィルタリングする
 */
function acceptFiles(accept: string[] | string, hideAcceptTypes: string[]) {
  if (typeof accept == 'string') return accept.split('/')[1]
  return accept
    .filter(type => {
      return hideAcceptTypes.some(hideAcceptType => type !== hideAcceptType)
    })
    .map(a => a.split('/')[1])
    .join(',')
}

/**
 * 拡張子毎のファイルサイズのチェック
 * 拡張子に該当し、ファイルサイズが満たせば、配列を返す。
 */
function validateFileSize(file) {
  return !!Object.entries(acceptExt).filter(
    ([key, types]) =>
      types.includes(file.type) && file.size <= uploadFileSize[key]
  ).length
}

const Area = styled.div({
  height: 68,
  borderRadius: '10px',
  border: '1px solid #E0E0E0',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
})
